import React, { useEffect, useState } from "react";
import { ArrowRight, Bell } from "lucide-react";

import { listActionTypes } from "@/api/actions";
import { Badge } from "@/components/primitives/Badge";
import { Button } from "@/components/primitives/Button";
import { Card } from "@/components/primitives/Card";
import { Header } from "@/components/layout/Header";
import { PageShell } from "@/components/layout/PageShell";

const ACTION_PAGES: Record<string, string> = {
  bale_bot: "/bale-bot",
  sms: "/sms",
};

export const ActionsPage: React.FC = () => {
  const [actions, setActions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setIsLoading(true);
        setError(null);
        const types = await listActionTypes();
        if (!cancelled) setActions(types);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load actions");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <PageShell>
      <Header
        action={
          <Button variant="secondary" onClick={() => (window.location.href = "/")}>
            Flows
          </Button>
        }
      />

      <main className="page-container py-10">
        <div className="mb-8">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
            Actions
          </h2>
          <p className="mt-1 text-slate-500 dark:text-slate-400">
            Registered alert actions and their configuration.
          </p>
        </div>

        {isLoading && (
          <div className="py-20 text-center text-slate-500 dark:text-slate-400">
            Loading actions…
          </div>
        )}
        {error && (
          <div
            className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
            role="alert"
          >
            {error}
          </div>
        )}

        {!isLoading && actions.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50/50 py-20 text-center dark:border-slate-700 dark:bg-slate-900/30">
            <h3 className="mb-2 text-xl font-semibold text-slate-900 dark:text-white">
              No actions registered
            </h3>
            <p className="max-w-sm text-slate-500 dark:text-slate-400">
              No alert actions are available right now.
            </p>
          </div>
        )}

        {!isLoading && actions.length > 0 && (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {actions.map((actionType) => {
              const configPath = ACTION_PAGES[actionType];
              return (
                <Card key={actionType} className="flex flex-col p-6">
                  <div className="mb-4 flex items-start justify-between">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-softnu-50 text-softnu-600 dark:bg-softnu-900/30 dark:text-softnu-300">
                      <Bell className="h-5 w-5" />
                    </div>
                    <Badge variant="neutral">Action</Badge>
                  </div>
                  <h3 className="mb-2 text-lg font-semibold text-slate-900 dark:text-white">
                    {actionType === "sms" ? "SMS alerts" : actionType}
                  </h3>
                  <p className="mb-6 flex-1 text-sm text-slate-500 dark:text-slate-400">
                    {actionType === "bale_bot"
                      ? "Broadcast failure alerts to authorized Bale bot subscribers."
                      : actionType === "sms" ? "Send failure SMS through SMS.ir to selected contacts with a custom message." : "No description available for this action."}
                  </p>
                  {configPath ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => (window.location.href = configPath)}
                      className="w-full gap-1.5"
                    >
                      Configure
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  ) : (
                    <span className="text-sm text-slate-400 dark:text-slate-500">
                      No configuration page available.
                    </span>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </PageShell>
  );
};
